import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { profileApi } from '@internal/api/profile';
import { useApiContext } from '@internal/components/common/api-context';
import { EnrichmentResult, EnrichmentStatus } from '@internal/types/api';

import {
  clearEnrichmentJob,
  ENRICHMENT_JOB_STORED_EVENT,
  PendingEnrichmentJob,
  readEnrichmentJob,
} from './enrichment-job-storage';

const POLL_INTERVAL = 5000;

const PARSED_QUERY_KEYS = [
  ['profile'],
  ['projects'],
  ['educations'],
  ['specializations'],
  ['application-state'],
  ['candidate-roles'],
];

const isTerminal = (status?: EnrichmentStatus) =>
  status === EnrichmentStatus.Finished ||
  status === EnrichmentStatus.FinishedWithErrors;

const EnrichmentWatcher: React.FC = () => {
  const queryClient = useQueryClient();
  const { profile } = useApiContext();
  const [stored, setStored] = useState<PendingEnrichmentJob | null>(null);

  useEffect(() => {
    const syncJob = () => setStored(readEnrichmentJob());

    syncJob();
    window.addEventListener(ENRICHMENT_JOB_STORED_EVENT, syncJob);
    return () =>
      window.removeEventListener(ENRICHMENT_JOB_STORED_EVENT, syncJob);
  }, []);

  const job = stored && profile?.apiKey === stored.apiKey ? stored : null;

  useEffect(() => {
    if (stored && profile && profile.apiKey !== stored.apiKey) {
      clearEnrichmentJob();
      setStored(null);
    }
  }, [stored, profile]);

  const { enrichmentJob } = profileApi.getEnrichmentJob.useQuery(
    job?.jobId ? { apiKey: job.apiKey, jobId: job.jobId } : undefined,
    {
      enabled: Boolean(job?.jobId),
      refetchInterval: query => {
        const status = (query.state.data as EnrichmentResult | null)?.status;
        return isTerminal(status) ? false : POLL_INTERVAL;
      },
    }
  );

  useEffect(() => {
    if (!job || !isTerminal(enrichmentJob?.status)) return;

    clearEnrichmentJob();
    setStored(null);

    PARSED_QUERY_KEYS.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey });
    });
  }, [job, enrichmentJob?.status, queryClient]);

  return null;
};

export default EnrichmentWatcher;
